import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { X, Minus, Plus, Loader2, Tag, Check, Mail, Truck, Zap, Lock } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import api from "../api";
import ProductImage from "./ProductImage";
import { PRELAUNCH_MODE } from "./PreLaunchBanner";
import NotifyMeModal from "./NotifyMeModal";
import CartTierNudge from "./CartTierNudge";
import CartStackHint from "./CartStackHint";
import CartSynergyHint from "./CartSynergyHint";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

const EMPTY_ADDR = {
  name: "", street1: "", street2: "", city: "", state: "", zip: "", phone: "",
};

export default function CartDrawer() {
  const { open, setOpen, items, updateQty, removeItem, addItem, subtotal, tierSavings, wholesale, totalKits } = useCart();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [promo, setPromo] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null); // {code, percent_off}
  const [promoErr, setPromoErr] = useState("");
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [payVenmo, setPayVenmo] = useState(false);
  const [venmoAck, setVenmoAck] = useState(false);  // acknowledges Venmo "Each" UI

  // Reset Venmo acknowledgement whenever the customer toggles Venmo off
  useEffect(() => {
    if (!payVenmo) setVenmoAck(false);
  }, [payVenmo]);

  // Address + shipping method
  const [addr, setAddr] = useState(() => {
    try {
      const cached = localStorage.getItem("psl_shipping_addr");
      return cached ? { ...EMPTY_ADDR, ...JSON.parse(cached) } : EMPTY_ADDR;
    } catch {
      return EMPTY_ADDR;
    }
  });
  const [shippingMethod, setShippingMethod] = useState("standard"); // "standard" | "overnight"

  // Live quote from backend (authoritative source of truth for total)
  const [quote, setQuote] = useState(null);
  const [quoteErr, setQuoteErr] = useState("");

  const updAddr = (k) => (e) =>
    setAddr((prev) => ({ ...prev, [k]: e.target.value }));

  // Persist address as user types (so reload doesn't wipe it)
  useEffect(() => {
    try {
      localStorage.setItem("psl_shipping_addr", JSON.stringify(addr));
    } catch { /* noop */ }
  }, [addr]);

  // Disable overnight if state is AK/HI; force back to standard
  useEffect(() => {
    if (shippingMethod === "overnight" && ["AK", "HI"].includes(addr.state)) {
      setShippingMethod("standard");
    }
  }, [addr.state, shippingMethod]);

  useEffect(() => {
    if (appliedPromo && items.length === 0) {
      setAppliedPromo(null);
      setPromo("");
    }
  }, [items, appliedPromo]);

  // Codes never apply to wholesale orders.
  useEffect(() => {
    if (wholesale && appliedPromo) { setAppliedPromo(null); setPromo(""); }
  }, [wholesale, appliedPromo]);

  // Build the quote payload (memoized so effect only fires on real changes)
  const quotePayload = useMemo(() => {
    if (items.length === 0) return null;
    return {
      items: items.map((i) => ({
        product_id: i.product_id, variant: i.variant, quantity: i.quantity,
      })),
      promo_code: appliedPromo?.code || null,
      shipping_method: shippingMethod,
      shipping_state: addr.state || null,
      payment_method: payVenmo ? "venmo" : "paypal",
      wholesale: wholesale,
    };
  }, [items, appliedPromo, shippingMethod, addr.state, payVenmo, wholesale]);

  // Live cart preview — refetch when payload changes (debounced)
  const fetchQuote = useCallback(async () => {
    if (!quotePayload) { setQuote(null); return; }
    try {
      const { data } = await api.post("/checkout/quote", quotePayload);
      setQuote(data);
      setQuoteErr("");
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (quotePayload.promo_code && typeof detail === "string" && /promo|code/i.test(detail)) {
        // Cart no longer qualifies (e.g. raised to 5+ vials) — drop the code, keep checkout working.
        setAppliedPromo(null);
        setPromoErr(detail);
        return;
      }
      setQuote(null);
      setQuoteErr(detail);
    }
  }, [quotePayload]);

  useEffect(() => {
    const t = setTimeout(fetchQuote, 250);
    return () => clearTimeout(t);
  }, [fetchQuote]);

  const applyPromo = async () => {
    setPromoErr("");
    if (!promo.trim()) return;
    try {
      const { data } = await api.post("/discount/validate", {
        code: promo.trim(),
        subtotal,
      });
      // Confirm against the real cart (wholesale / 5+ vial rules live server-side).
      await api.post("/checkout/quote", { ...quotePayload, promo_code: data.code });
      setAppliedPromo(data);
    } catch (e) {
      setPromoErr(e?.response?.data?.detail || "Invalid promo code.");
      setAppliedPromo(null);
    }
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setPromo("");
    setPromoErr("");
  };

  // Use backend quote for totals if available, else fall back to client calc
  const promoDiscount = quote?.promo_discount ?? (appliedPromo
    ? Math.round(subtotal * (appliedPromo.percent_off / 100) * 100) / 100
    : 0);
  const venmoDiscount = quote?.venmo_discount ?? 0;
  const shippingFee = quote?.shipping_fee ?? 0;
  const taxAmount = quote?.tax_amount ?? 0;
  const total = quote?.total ?? Math.max(0, subtotal - promoDiscount);
  const vipFree = /^VIP-[A-F0-9]{6}$/i.test(appliedPromo?.code || "") && quote && quote.total === 0;

  // Address must be fully filled to allow checkout
  const addressValid =
    addr.name.trim().length >= 2 &&
    addr.street1.trim().length >= 3 &&
    addr.city.trim().length >= 2 &&
    addr.state.length === 2 &&
    /^\d{5}(-\d{4})?$/.test(addr.zip.trim());

  const checkout = async () => {
    setErr("");
    if (wholesale && totalKits < 5) {
      setErr(`Wholesale minimum is 5 kits total. You have ${totalKits} kit${totalKits === 1 ? "" : "s"}.`);
      return;
    }
    if (!addressValid) {
      setErr("Please complete your shipping address.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          variant: i.variant,
          quantity: i.quantity,
        })),
        origin_url: window.location.origin,
        promo_code: appliedPromo?.code || null,
        shipping_method: shippingMethod,
        shipping_address: {
          name: addr.name.trim(),
          street1: addr.street1.trim(),
          street2: addr.street2.trim() || null,
          city: addr.city.trim(),
          state: addr.state.toUpperCase(),
          zip: addr.zip.trim(),
          phone: addr.phone.trim() || null,
        },
        wholesale: wholesale,
      };
      if (vipFree) {
        const { data } = await api.post("/checkout/vip-order", payload);
        setOpen(false);
        window.location.href = `/account/orders/${encodeURIComponent(data.order_id)}`;
        return;
      }
      if (payVenmo) {
        // Manual P2P Venmo flow — bypasses PayPal entirely, pending admin verification
        const { data } = await api.post("/checkout/venmo-manual", {
          ...payload,
          venmo_each_acknowledged: venmoAck,
        });
        setOpen(false);
        window.location.href = `/checkout/venmo-pending?order=${encodeURIComponent(data.order_number)}&autopay=1`;
        return;
      }
      // PayPal hosted checkout
      const { data } = await api.post("/checkout/paypal/create-order", {
        ...payload,
        payment_method: "paypal",
      });
      if (data.approve_url) window.location.href = data.approve_url;
      else throw new Error("Checkout temporarily unavailable.");
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Unable to start checkout.");
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div data-testid="cart-drawer" className="fixed inset-0 z-50 flex justify-end" onClick={() => setOpen(false)}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <aside
        className="relative w-full sm:w-[480px] h-full bg-[#0C0C12] border-l border-white/10 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <div className="overline">YOUR CART</div>
            <div className="font-heading text-2xl mt-1">{items.length} item{items.length !== 1 ? "s" : ""}</div>
          </div>
          <button data-testid="cart-close-button" onClick={() => setOpen(false)} className="text-white/60 hover:text-white">
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-5">
          {wholesale && items.length > 0 && (
            <div
              data-testid="cart-wholesale-banner"
              className="p-4 border border-[#00C3FF]/40 bg-[#00C3FF]/5"
            >
              <div className="flex items-center justify-between">
                <div className="overline text-[#00C3FF] text-[10px]">WHOLESALE ORDER</div>
                <div className="font-mono-tech text-[10px] text-[#8A94A6] uppercase tracking-wider">
                  {totalKits} / 5 kits min
                </div>
              </div>
              {totalKits < 5 && (
                <p className="text-[11px] text-yellow-300 mt-2 leading-relaxed">
                  Add {5 - totalKits} more kit{5 - totalKits === 1 ? "" : "s"} to meet the wholesale
                  minimum (5 kits total · mix &amp; match any products).
                </p>
              )}
              <p
                data-testid="cart-wholesale-processing-notice"
                className="text-[11px] text-[#8A94A6] mt-2 leading-relaxed"
              >
                <span className="font-mono-tech text-[#00C3FF] text-[10px] uppercase tracking-wider">Processing time:</span>{" "}
                Wholesale orders require up to 2–3 business days to process — often sooner.
              </p>
            </div>
          )}

          {items.length === 0 ? (
            <div className="text-center text-[#8A94A6] py-20">
              <div className="overline mb-3">EMPTY</div>
              <p className="text-sm">Add research compounds to begin checkout.</p>
            </div>
          ) : (
            items.map((it) => (
              <div
                key={`${it.product_id}-${it.variant}`}
                data-testid={`cart-item-${it.slug}-${it.variant}`}
                className="flex gap-4 surface p-4"
              >
                <div className="w-20 h-20 vial-bg flex items-center justify-center flex-shrink-0">
                  <ProductImage product={it} className="w-[80%] h-[80%]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-base">{it.name}</div>
                  <div className="font-mono-tech text-[11px] text-[#00C3FF] tracking-wider uppercase">
                    {it.variant}{it.is_wholesale ? " · WHOLESALE" : ""}
                  </div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="font-mono-tech text-sm">${(it.line_total).toFixed(2)}</span>
                    {!it.is_wholesale && it.tier_savings > 0 && (
                      <span className="font-mono-tech text-[10px] text-[#5C657A] line-through">
                        ${(it.list_total).toFixed(2)}
                      </span>
                    )}
                  </div>
                  {it.is_wholesale ? (
                    <div className="font-mono-tech text-[10px] text-[#00C3FF] mt-0.5">
                      ${(it.unit_price * 10).toFixed(2)}/kit · {it.quantity / 10} kit{it.quantity / 10 === 1 ? "" : "s"}
                    </div>
                  ) : it.tier_savings > 0 && (
                    <div className="font-mono-tech text-[10px] text-[#00C3FF] mt-0.5">
                      Tier price · ${it.unit_price.toFixed(2)}/unit
                    </div>
                  )}
                  <CartTierNudge item={it} onUpgrade={(q) => updateQty(it.product_id, it.variant, q)} />
                  <div className="flex items-center gap-3 mt-2">
                    <button
                      data-testid={`cart-decrement-${it.slug}`}
                      onClick={() => updateQty(it.product_id, it.variant, it.quantity - (it.is_wholesale ? 10 : 1))}
                      className="w-7 h-7 border border-white/20 hover:border-[#00C3FF] flex items-center justify-center"
                    ><Minus size={12} /></button>
                    <span className="font-mono-tech text-sm w-12 text-center" data-testid={`cart-qty-${it.slug}`}>
                      {it.is_wholesale ? `${it.quantity / 10} kit${it.quantity / 10 === 1 ? "" : "s"}` : it.quantity}
                    </span>
                    <button
                      data-testid={`cart-increment-${it.slug}`}
                      onClick={() => updateQty(it.product_id, it.variant, it.quantity + (it.is_wholesale ? 10 : 1))}
                      className="w-7 h-7 border border-white/20 hover:border-[#00C3FF] flex items-center justify-center"
                    ><Plus size={12} /></button>
                    <button
                      onClick={() => removeItem(it.product_id, it.variant)}
                      className="ml-auto text-[10px] font-mono-tech text-[#5C657A] hover:text-red-400 uppercase tracking-wider"
                    >Remove</button>
                  </div>
                </div>
              </div>
            ))
          )}

        <CartStackHint items={items} addItem={addItem} wholesale={wholesale} />
        <CartSynergyHint items={items} addItem={addItem} wholesale={wholesale} />

        {items.length > 0 && (
          <div className="pt-2 space-y-3 border-t border-white/10">
            <div>
              <div className="overline mb-2">PROMO CODE</div>
              {wholesale ? (
                <div data-testid="cart-promo-wholesale-note" className="text-[11px] font-mono-tech text-[#8A94A6]">Discount codes can't be used on wholesale orders.</div>
              ) : appliedPromo ? (
                <div data-testid="cart-promo-applied" className="flex items-center justify-between surface p-3">
                  <div className="flex items-center gap-2 text-[#00C3FF] font-mono-tech text-xs uppercase tracking-wider">
                    <Check size={14} /> {appliedPromo.code} · {appliedPromo.percent_off}% off
                  </div>
                  <button onClick={removePromo} className="text-[10px] font-mono-tech text-[#5C657A] hover:text-red-400 uppercase">Remove</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    data-testid="cart-promo-input"
                    value={promo}
                    onChange={(e) => setPromo(e.target.value.toUpperCase())}
                    placeholder="Enter code (optional)"
                    className="input-tech flex-1"
                  />
                  <button
                    data-testid="cart-promo-apply"
                    onClick={applyPromo}
                    className="btn-ghost text-[10px]"
                    style={{ padding: "10px 16px" }}
                  ><Tag size={12} /> Apply</button>
                </div>
              )}
              {promoErr && <div data-testid="cart-promo-error" className="text-[10px] font-mono-tech text-red-400 mt-2">{String(promoErr)}</div>}
            </div>

            <div data-testid="cart-shipping-address" className="pt-2">
              <div className="overline mb-2">SHIP TO</div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  data-testid="cart-addr-name"
                  className="input-tech col-span-2"
                  placeholder="Full name"
                  value={addr.name}
                  onChange={updAddr("name")}
                  autoComplete="name"
                />
                <input
                  data-testid="cart-addr-street1"
                  className="input-tech col-span-2"
                  placeholder="Street address"
                  value={addr.street1}
                  onChange={updAddr("street1")}
                  autoComplete="address-line1"
                />
                <input
                  data-testid="cart-addr-street2"
                  className="input-tech col-span-2"
                  placeholder="Apt / Suite (optional)"
                  value={addr.street2}
                  onChange={updAddr("street2")}
                  autoComplete="address-line2"
                />
                <input
                  data-testid="cart-addr-city"
                  className="input-tech"
                  placeholder="City"
                  value={addr.city}
                  onChange={updAddr("city")}
                  autoComplete="address-level2"
                />
                <select
                  data-testid="cart-addr-state"
                  className="input-tech"
                  value={addr.state}
                  onChange={updAddr("state")}
                  style={{ colorScheme: "dark" }}
                  autoComplete="address-level1"
                >
                  <option value="">State</option>
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <input
                  data-testid="cart-addr-zip"
                  className="input-tech"
                  placeholder="ZIP"
                  value={addr.zip}
                  onChange={updAddr("zip")}
                  inputMode="numeric"
                  maxLength={10}
                  autoComplete="postal-code"
                />
                <input
                  data-testid="cart-addr-phone"
                  className="input-tech"
                  placeholder="Phone (optional)"
                  value={addr.phone}
                  onChange={updAddr("phone")}
                  inputMode="tel"
                  autoComplete="tel"
                />
              </div>
              <div className="text-[10px] font-mono-tech text-[#5C657A] mt-2 uppercase tracking-wider">
                Ships from Hemet, CA · USPS only
              </div>
            </div>

            <div data-testid="cart-shipping-method" className="pt-1">
              <div className="overline mb-2">SHIPPING METHOD</div>
              <div className="grid grid-cols-2 gap-2">
                <label
                  data-testid="cart-ship-standard-label"
                  className={`cursor-pointer p-3 border transition-colors ${shippingMethod === "standard" ? "border-[#00C3FF] bg-[#00C3FF]/5" : "border-white/10 hover:border-[#00C3FF]/40"}`}
                >
                  <input
                    type="radio"
                    name="ship"
                    data-testid="cart-ship-standard"
                    checked={shippingMethod === "standard"}
                    onChange={() => setShippingMethod("standard")}
                    className="sr-only"
                  />
                  <div className="flex items-center gap-2">
                    <Truck size={14} className="text-[#00C3FF]" />
                    <span className="font-mono-tech text-[11px] uppercase tracking-wider text-white">Standard</span>
                  </div>
                  <div className="text-[10px] text-[#8A94A6] mt-1">USPS 2-3 day</div>
                  <div className="font-mono-tech text-[11px] text-[#00C3FF] mt-1">
                    {quote && quote.shipping_method === "standard" && quote.shipping_fee === 0
                      ? "FREE"
                      : "$12.99"}
                  </div>
                </label>
                <label
                  data-testid="cart-ship-overnight-label"
                  className={`p-3 border transition-colors ${["AK","HI"].includes(addr.state) ? "border-white/5 bg-white/5 opacity-40 cursor-not-allowed" : "cursor-pointer"} ${shippingMethod === "overnight" ? "border-[#00C3FF] bg-[#00C3FF]/5" : "border-white/10 hover:border-[#00C3FF]/40"}`}
                >
                  <input
                    type="radio"
                    name="ship"
                    data-testid="cart-ship-overnight"
                    checked={shippingMethod === "overnight"}
                    onChange={() => setShippingMethod("overnight")}
                    disabled={["AK","HI"].includes(addr.state)}
                    className="sr-only"
                  />
                  <div className="flex items-center gap-2">
                    <Zap size={14} className="text-[#00C3FF]" />
                    <span className="font-mono-tech text-[11px] uppercase tracking-wider text-white">Overnight</span>
                  </div>
                  <div className="text-[10px] text-[#8A94A6] mt-1">
                    {["AK","HI"].includes(addr.state) ? "Lower 48 only" : "USPS Express"}
                  </div>
                  <div className="font-mono-tech text-[11px] text-[#00C3FF] mt-1">$49.99</div>
                </label>
              </div>
              {subtotal < 150 && shippingMethod === "standard" && (
                <div className="text-[10px] font-mono-tech text-[#5C657A] mt-2 uppercase tracking-wider">
                  Free Standard shipping on orders $150+
                </div>
              )}
            </div>

            <div className="space-y-1 text-sm pt-2">
            <div className="flex justify-between text-white/70">
              <span className="font-mono-tech text-[11px] uppercase tracking-[0.18em] text-[#5C657A]">Subtotal</span>
              <span className="font-mono-tech" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
            </div>
            {tierSavings > 0 && (
              <div className="flex justify-between text-[#00C3FF]">
                <span className="font-mono-tech text-[11px] uppercase tracking-[0.18em]">Volume Savings</span>
                <span className="font-mono-tech" data-testid="cart-tier-savings">-${tierSavings.toFixed(2)}</span>
              </div>
            )}
            {quote?.stack_discount > 0 && (
              <div data-testid="cart-stack-discount" className="flex justify-between text-[#00C3FF]">
                <span className="font-mono-tech text-[11px] uppercase tracking-[0.18em]">Research Stack</span>
                <span className="font-mono-tech">-${quote.stack_discount.toFixed(2)}</span>
              </div>
            )}
            {promoDiscount > 0 && (
              <div className="flex justify-between text-[#00C3FF]">
                <span className="font-mono-tech text-[11px] uppercase tracking-[0.18em]">Promo</span>
                <span className="font-mono-tech">-${promoDiscount.toFixed(2)}</span>
              </div>
            )}
            {venmoDiscount > 0 && (
              <div className="flex justify-between text-[#00C3FF]" data-testid="cart-venmo-discount-row">
                <span className="font-mono-tech text-[11px] uppercase tracking-[0.18em]">Venmo Discount · 2%</span>
                <span className="font-mono-tech" data-testid="cart-venmo-discount">-${venmoDiscount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-white/70" data-testid="cart-shipping-row">
              <span className="font-mono-tech text-[11px] uppercase tracking-[0.18em] text-[#5C657A]">
                Shipping {shippingMethod === "overnight" ? "· Overnight" : "· Standard"}
              </span>
              <span className="font-mono-tech" data-testid="cart-shipping-fee">
                {shippingFee === 0 && quote?.shipping_method ? "FREE" : `$${shippingFee.toFixed(2)}`}
              </span>
            </div>
            {taxAmount > 0 && (
              <div className="flex justify-between text-white/70" data-testid="cart-tax-row">
                <span className="font-mono-tech text-[11px] uppercase tracking-[0.18em] text-[#5C657A]">
                  CA Sales Tax · {quote?.tax_rate ? `${(quote.tax_rate * 100).toFixed(2)}%` : "9.5%"}
                </span>
                <span className="font-mono-tech" data-testid="cart-tax-amount">${taxAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between items-baseline pt-2 border-t border-white/5 mt-2">
              <span className="overline">TOTAL</span>
              <span data-testid="cart-total" className="font-mono-tech text-2xl">${total.toFixed(2)}</span>
            </div>
            {quoteErr && (
              <div className="text-[10px] font-mono-tech text-red-400 mt-2" data-testid="cart-quote-error">
                {String(quoteErr)}
              </div>
            )}
          </div>

          {err && <div className="text-xs text-red-400 font-mono-tech">{String(err)}</div>}

          <label
            data-testid="cart-agree-label"
            className="flex items-start gap-3 cursor-pointer select-none p-3 border border-white/10 hover:border-[#00C3FF]/40 transition-colors"
          >
            <input
              type="checkbox"
              data-testid="cart-agree-checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 accent-[#00C3FF] flex-shrink-0"
            />
            <span className="text-[11px] text-[#8A94A6] leading-relaxed">
              I am 21+ and confirm these products are for{" "}
              <span className="text-white">research use only</span> — never for human or animal
              consumption. I have read and agree to the{" "}
              <Link
                to="/disclaimer"
                onClick={() => setOpen(false)}
                className="text-[#00C3FF] hover:underline"
                data-testid="cart-link-disclaimer"
              >
                Disclaimer
              </Link>{" "}
              and{" "}
              <Link
                to="/terms"
                onClick={() => setOpen(false)}
                className="text-[#00C3FF] hover:underline"
                data-testid="cart-link-terms"
              >
                Terms &amp; Conditions
              </Link>
              .
            </span>
          </label>

          {!(PRELAUNCH_MODE && user?.role !== "admin") && (
            <label
              data-testid="cart-venmo-label"
              className={`flex items-start gap-3 cursor-pointer select-none p-3 border transition-colors ${payVenmo ? "border-[#00C3FF] bg-[#00C3FF]/5" : "border-white/10 hover:border-[#00C3FF]/40"}`}
            >
              <input
                type="checkbox"
                data-testid="cart-venmo-checkbox"
                checked={payVenmo}
                onChange={(e) => setPayVenmo(e.target.checked)}
                className="mt-0.5 accent-[#00C3FF] flex-shrink-0"
              />
              <span className="text-[11px] text-[#8A94A6] leading-relaxed flex-1">
                <span className="text-white font-medium">Pay with Venmo instead — Save 2%</span>
                <br />
                <span className="text-[10px] text-[#5C657A]">
                  You'll send the total to{" "}
                  <span className="text-[#00C3FF] font-mono-tech">@prosourcelabs</span>{" "}
                  in the Venmo app · ships within 24h of payment verification.
                </span>
              </span>
            </label>
          )}

          {payVenmo && !(PRELAUNCH_MODE && user?.role !== "admin") && (
            <label
              data-testid="cart-venmo-ack-label"
              className={`flex items-start gap-3 cursor-pointer select-none p-4 border-2 transition-colors ${venmoAck ? "border-[#00C3FF] bg-[#00C3FF]/5" : "border-yellow-500/60 bg-yellow-500/5"}`}
            >
              <input
                type="checkbox"
                data-testid="cart-venmo-ack-checkbox"
                checked={venmoAck}
                onChange={(e) => setVenmoAck(e.target.checked)}
                className="mt-0.5 accent-[#00C3FF] flex-shrink-0"
              />
              <span className="text-[11px] text-[#8A94A6] leading-relaxed flex-1">
                <span className="font-mono-tech text-yellow-400 uppercase tracking-wider text-[10px]">⚠ Required for Venmo</span>
                <br />
                <span className="text-white text-[11px] block mt-1">
                  I understand that Venmo's pay screen will display{" "}
                  <span className="font-mono-tech text-[#00C3FF]">${total.toFixed(2)}</span>{" "}
                  with the word <span className="font-mono-tech">"Each"</span> below it and{" "}
                  <span className="font-mono-tech text-red-300">${(total * 2).toFixed(2)} total</span>{" "}
                  shown as the 2-payment schedule.
                </span>
                <span className="text-[10px] text-[#8A94A6] block mt-2 leading-relaxed">
                  This is Venmo's default display for business accounts.{" "}
                  <span className="text-white">
                    I will be charged only ${total.toFixed(2)} — once — NOT ${(total * 2).toFixed(2)}.
                  </span>{" "}
                  I will tap <span className="text-white">Pay</span> and will NOT tap "Edit schedule"
                  or change the payment count.
                </span>
              </span>
            </label>
          )}

          {!(PRELAUNCH_MODE && user?.role !== "admin") && (
            <div
              data-testid="cart-memo-compliance-notice"
              className="text-[10px] text-[#8A94A6] leading-relaxed p-3 border border-yellow-500/30 bg-yellow-500/5"
            >
              <span className="font-mono-tech text-yellow-400 uppercase tracking-wider text-[10px]">Note ·</span>{" "}
              If a memo / message field appears at checkout (PayPal or Venmo), leave it{" "}
              <span className="text-white">blank</span> or paste only your{" "}
              <span className="text-white">order number</span>. Never type product or peptide
              names — orders with non-compliant memos may be refunded.
            </div>
          )}
        </div>
        )}
        </div>

        <div className="border-t border-white/10 bg-[#06060B] p-4 sm:p-5 space-y-2 flex-shrink-0">
          {PRELAUNCH_MODE && user?.role !== "admin" ? (
            <>
              <button
                data-testid="cart-prelaunch-notify"
                disabled={items.length > 0 && !agreed}
                onClick={() => setNotifyOpen(true)}
                className="btn-primary w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Mail size={16} />
                Notify Me at Launch
              </button>
              <p className="text-[10px] text-[#5C657A] font-mono-tech text-center uppercase tracking-[0.2em]">
                Pre-launch · Checkout opens soon · For Research Use Only
              </p>
            </>
          ) : (
            <>
              {PRELAUNCH_MODE && user?.role === "admin" && (
                <div
                  data-testid="cart-admin-test-badge"
                  className="text-[10px] font-mono-tech text-[#00C3FF] text-center uppercase tracking-[0.2em] border border-[#00C3FF]/40 bg-[#00C3FF]/5 py-2"
                >
                  Admin Test Mode · Real payment will be charged
                </div>
              )}
              {items.length > 0 && (
                <div className="flex items-baseline justify-between mb-1">
                  <span className="overline">TOTAL</span>
                  <span data-testid="cart-footer-total" className="font-mono-tech text-xl text-white">${total.toFixed(2)}</span>
                </div>
              )}
              <button
                data-testid="cart-checkout-button"
                disabled={items.length === 0 || busy || !agreed || !addressValid || (payVenmo && !venmoAck && !vipFree) || (wholesale && totalKits < 5)}
                onClick={checkout}
                className={
                  wholesale && totalKits < 5
                    ? "w-full justify-center inline-flex items-center gap-2 py-3 px-4 border border-red-500/50 bg-red-500/10 text-red-300 font-mono-tech text-xs uppercase tracking-[0.2em] cursor-not-allowed"
                    : "btn-primary w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                }
              >
                {wholesale && totalKits < 5 ? (
                  <>
                    <Lock size={14} />
                    Locked · Add {5 - totalKits} more kit{5 - totalKits === 1 ? "" : "s"} to checkout
                  </>
                ) : (
                  <>
                    {busy ? <Loader2 className="animate-spin" size={16} /> : null}
                    {busy ? "Redirecting..." : vipFree ? "Place VIP Order · $0 Due" : payVenmo ? "Continue to Venmo Instructions" : "Continue with PayPal"}
                  </>
                )}
              </button>
              {!addressValid && items.length > 0 && (
                <p className="text-[10px] text-yellow-400 font-mono-tech text-center uppercase tracking-[0.2em]">
                  Complete shipping address to checkout
                </p>
              )}
              {wholesale && totalKits < 5 && items.length > 0 && (
                <p className="text-[10px] text-yellow-400 font-mono-tech text-center uppercase tracking-[0.2em]" data-testid="cart-wholesale-moq-hint">
                  Wholesale minimum: 5 kits (50 vials) · You have {totalKits} kit{totalKits === 1 ? "" : "s"}
                </p>
              )}
              {addressValid && payVenmo && !venmoAck && items.length > 0 && (
                <p className="text-[10px] text-yellow-400 font-mono-tech text-center uppercase tracking-[0.2em]" data-testid="cart-venmo-ack-hint">
                  Acknowledge the Venmo "Each" notice above to continue
                </p>
              )}
              <p className="text-[10px] text-[#5C657A] font-mono-tech text-center uppercase tracking-[0.2em]">
                {payVenmo
                  ? "Pay @prosourcelabs · Manual verification · Ships within 24h"
                  : "PayPal · Billed as Helix Sci Hold · Research Use Only"}
              </p>
            </>
          )}
        </div>
      </aside>
      <NotifyMeModal
        open={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        items={items}
        source="cart"
        defaultEmail={user?.email || ""}
      />
    </div>
  );
}
